import { useState } from "react";
import { ArrowLeft, ArrowUpRight, CalendarClock, Download, Factory, LogOut, Receipt, ShieldCheck, Users } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { MODULES, platformClient } from "./platformApi";
import { ChangePassword } from "@/components/platform/ChangePassword";
import { ProductionModule } from "./ProductionModule";
import { PlanningModule } from "./PlanningModule";
import { CrmModule } from "./CrmModule";
import { InvoicingModule } from "./InvoicingModule";
import { DashboardOverview } from "./DashboardOverview";
import { MyBilling, BrandingCard } from "./OwnerExtras";

const ICONS = { production: Factory, crm: Users, planning: CalendarClock, invoicing: Receipt };

const TOUR_STEPS = [
    {
        title: "Welkom bij de demo",
        desc: "Je kijkt mee bij Van Dijk Metaal BV, een fictieve metaalwerker. Volg de stappen en ontdek wat het platform voor je doet.",
        module: null,
    },
    {
        title: "Van lead naar klant in één klik",
        desc: "Open de CRM, klap 'Camping Het Groene Hart' open en klik op 'Klant'. Zie hoe de gouden animatie en de tellers live reageren.",
        module: "crm",
    },
    {
        title: "Productie in één oogopslag",
        desc: "Bekijk order PO-0001 met stappen, machines en uren. Klik daarna op 'Recepturen & kostprijs': materiaal + arbeid + machine + overhead = je echte kostprijs.",
        module: "production",
    },
    {
        title: "Kostenanalyse per product",
        desc: "Open de tab 'Kostenanalyse': per categorie (grondstof, arbeid, energie, overhead) en per product zie je exact waar je geld zit — van kostprijs naar adviesprijs.",
        module: "production",
    },
    {
        title: "OEE: zo presteren je lijnen",
        desc: "De tab 'OEE' toont beschikbaarheid, prestatie en kwaliteit per lijn tegen het 85%-doel. Op het dashboard zie je de score elke ochtend direct.",
        module: "production",
    },
    {
        title: "Batch-tracering in één klik",
        desc: "In 'Batches (HACCP)' registreer je elke grondstof-batch en koppel je die aan orders. Traceer batch RVS-4471 en zie direct welke klanten die batch kregen.",
        module: "production",
    },
    {
        title: "Roosters die zichzelf opstellen",
        desc: "Het weekrooster toont wie wanneer werkt, inclusief verlof en beschikbaarheid per medewerker.",
        module: "planning",
    },
    {
        title: "Van offerte naar factuur",
        desc: "Verstuur een offerte per e-mail: de klant accepteert online en er rolt automatisch een productieorder uit. Gereed? Factureer de uren met één klik.",
        module: "invoicing",
    },
    {
        title: "Jouw merk, jouw betalingen",
        desc: "Terug op het overzicht: upload je eigen logo bij 'Huisstijl' (komt op offertes, facturen én het klantportaal) en bekijk 'Mijn abonnement' — online betalen met één klik.",
        module: null,
    },
    {
        title: "Klaar voor de volgende pitch",
        desc: "Met 'Demo resetten' in de beheeromgeving zet je alle data terug naar het beginpunt. Veel succes met je presentatie!",
        module: null,
    },
];

export const PlatformHome = ({ user, onLogout, onRefresh }) => {
    const [openModule, setOpenModule] = useState(null);
    const [tourStep, setTourStep] = useState(null);
    const enabled = MODULES.filter((m) => user.tenant?.modules?.[m.id]);
    const active = openModule ? MODULES.find((m) => m.id === openModule) : null;

    const goToStep = (idx) => {
        const step = TOUR_STEPS[idx];
        setTourStep(idx);
        setOpenModule(step.module);
    };

    const exportData = async () => {
        try {
            const { data, headers } = await platformClient.get("/data-export", { responseType: "blob" });
            const match = /filename="([^"]+)"/.exec(headers["content-disposition"] || "");
            const url = URL.createObjectURL(new Blob([data], { type: "application/json" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = match?.[1] || "data-export.json";
            a.click();
            URL.revokeObjectURL(url);
        } catch {}
    };

    return (
        <div data-testid="platform-home" className="min-h-screen bg-ink text-bone">
            <header className="sticky top-0 z-10 border-b border-white/10 bg-black/60 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-5">
                        <BrandLockup emblemClass="h-9 w-9" textClass="text-base" align="start" />
                        <span className="border-l border-white/15 pl-5 text-[11px] uppercase tracking-[0.3em] text-ash">
                            {user.tenant?.name}
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden text-xs text-ash sm:block">{user.email}</span>
                        <ChangePassword />
                        <button
                            data-testid="platform-home-logout"
                            onClick={onLogout}
                            className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Uitloggen
                        </button>
                    </div>
                </div>
            </header>
            {user.impersonating ? (
                <div data-testid="impersonation-banner" className="border-b border-gold/30 bg-gold/10">
                    <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-3 px-6 py-3">
                        <p className="text-xs uppercase tracking-[0.2em] text-gold">
                            Je bekijkt {user.tenant?.name} als beheerder
                        </p>
                        <button
                            data-testid="stop-impersonation-button"
                            onClick={async () => {
                                await platformClient.post("/admin/stop-impersonating");
                                onRefresh();
                            }}
                            className="border border-gold/50 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black"
                        >
                            Terug naar beheer
                        </button>
                    </div>
                </div>
            ) : null}

            <div className="mx-auto max-w-[1200px] px-6 py-12">
                {active ? (
                    <div>
                        <button
                            data-testid="module-back-button"
                            onClick={() => setOpenModule(null)}
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:text-gold"
                        >
                            <ArrowLeft className="h-4 w-4" /> Overzicht
                        </button>
                        {active.id === "production" ? (
                            <ProductionModule />
                        ) : active.id === "planning" ? (
                            <PlanningModule user={user} />
                        ) : active.id === "crm" ? (
                            <CrmModule />
                        ) : active.id === "invoicing" ? (
                            <InvoicingModule />
                        ) : (
                        <div
                            data-testid={`module-stub-${active.id}`}
                            className="mt-8 border border-white/10 bg-coal p-10 md:p-16"
                        >
                            {(() => {
                                const Icon = ICONS[active.id];
                                return <Icon className="h-8 w-8 text-gold" strokeWidth={1.5} />;
                            })()}
                            <h2 className="mt-6 font-display text-3xl tracking-tight md:text-4xl">
                                {active.label}
                            </h2>
                            <p className="mt-4 max-w-md text-sm leading-relaxed text-ash">
                                {active.desc}
                            </p>
                            <p className="mt-8 inline-block border border-gold/30 bg-gold/5 px-5 py-3 text-xs uppercase tracking-[0.2em] text-gold">
                                Deze module wordt momenteel gebouwd
                            </p>
                        </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.35em] text-gold">
                            Welkom{user.name ? `, ${user.name}` : ""}
                        </p>
                        <h1 className="mt-4 font-display text-4xl tracking-tight md:text-5xl">
                            Jouw <span className="italic text-gold">modules</span>
                        </h1>
                        {user.tenant?.is_demo ? (
                            <button
                                data-testid="demo-tour-start"
                                onClick={() => goToStep(0)}
                                className="mt-6 flex items-center gap-2 border border-gold/50 bg-gold/5 px-6 py-3 text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-black"
                            >
                                Start presentatiemodus
                            </button>
                        ) : null}
                        <DashboardOverview onOpenModule={setOpenModule} />
                        {enabled.length === 0 ? (
                            <p data-testid="platform-no-modules" className="mt-10 max-w-md text-sm leading-relaxed text-ash">
                                Er zijn nog geen modules actief voor jouw omgeving. Neem contact op
                                met Clarke Business Solutions om modules te activeren.
                            </p>
                        ) : (
                            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                                {enabled.map((m) => {
                                    const Icon = ICONS[m.id];
                                    return (
                                        <button
                                            key={m.id}
                                            data-testid={`module-card-${m.id}`}
                                            onClick={() => setOpenModule(m.id)}
                                            className="group relative overflow-hidden border border-white/10 bg-coal p-8 text-left transition-colors duration-500 hover:border-gold/40"
                                        >
                                            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gold/0 blur-3xl transition-colors duration-700 group-hover:bg-gold/10" />
                                            <div className="flex items-start justify-between">
                                                <Icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                                                <ArrowUpRight className="h-5 w-5 text-ash opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:text-gold group-hover:opacity-100" />
                                            </div>
                                            <h3 className="mt-10 font-display text-2xl tracking-tight">
                                                {m.label}
                                            </h3>
                                            <p className="mt-2 text-sm leading-relaxed text-ash">
                                                {m.desc}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {user.role === "owner" ? (
                            <div className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-2">
                                <MyBilling />
                                <BrandingCard />
                            </div>
                        ) : null}
                        {user.role === "owner" ? (
                            <div data-testid="avg-section" className="mt-4 border border-white/10 bg-coal p-6 md:p-8">
                                <p className="text-[10px] uppercase tracking-[0.25em] text-ash">
                                    <ShieldCheck className="mr-2 inline h-3.5 w-3.5 text-gold" />
                                    Privacy &amp; gegevens (AVG)
                                </p>
                                <p className="mt-3 max-w-lg text-sm leading-relaxed text-ash">
                                    Jouw gegevens blijven van jou. Download op elk moment een volledige export van alles in je omgeving — orders, klanten, facturen, voorraad en planning — of lees hoe wij met gegevens omgaan.
                                </p>
                                <div className="mt-5 flex flex-wrap items-center gap-3">
                                    <button
                                        data-testid="data-export-button"
                                        onClick={exportData}
                                        className="flex items-center gap-2 border border-gold/50 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                    >
                                        <Download className="h-3.5 w-3.5" /> Exporteer al mijn gegevens
                                    </button>
                                    <a
                                        data-testid="avg-dpa-link"
                                        href="/verwerkersovereenkomst"
                                        className="border border-white/15 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                                    >
                                        Verwerkersovereenkomst
                                    </a>
                                    <a
                                        data-testid="avg-privacy-link"
                                        href="/privacy"
                                        className="border border-white/15 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                                    >
                                        Privacyverklaring
                                    </a>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            {tourStep !== null ? (
                <div data-testid="demo-tour-panel" className="fixed bottom-6 right-6 z-20 w-80 border border-gold/30 bg-coal/95 p-6 shadow-2xl shadow-black/60 backdrop-blur-xl">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                        Stap {tourStep + 1} van {TOUR_STEPS.length}
                    </p>
                    <h3 data-testid="demo-tour-title" className="mt-3 font-display text-xl tracking-tight">
                        {TOUR_STEPS[tourStep].title}
                    </h3>
                    <p data-testid="demo-tour-desc" className="mt-2 text-sm leading-relaxed text-ash">
                        {TOUR_STEPS[tourStep].desc}
                    </p>
                    <div className="mt-5 flex items-center justify-between gap-2">
                        <button
                            data-testid="demo-tour-prev"
                            onClick={() => goToStep(tourStep - 1)}
                            disabled={tourStep === 0}
                            className="border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-30"
                        >
                            Vorige
                        </button>
                        {tourStep < TOUR_STEPS.length - 1 ? (
                            <button
                                data-testid="demo-tour-next"
                                onClick={() => goToStep(tourStep + 1)}
                                className="border border-gold/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black"
                            >
                                Volgende
                            </button>
                        ) : (
                            <button
                                data-testid="demo-tour-finish"
                                onClick={() => { setTourStep(null); setOpenModule(null); }}
                                className="border border-gold bg-gold px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-black transition-colors hover:bg-transparent hover:text-gold"
                            >
                                Afronden
                            </button>
                        )}
                    </div>
                    <button
                        data-testid="demo-tour-close"
                        onClick={() => setTourStep(null)}
                        className="mt-3 w-full text-center text-[10px] uppercase tracking-[0.25em] text-ash/60 transition-colors hover:text-ash"
                    >
                        Sluiten
                    </button>
                </div>
            ) : null}
        </div>
    );
};
